import { createTheme } from '@mui/material/styles'

// Extending the Palette interface to include custom colors
declare module '@mui/material/styles' {
  interface Palette {
    customColors: {
      green1: string
      green2: string
      green3: string
      green4: string
      darkSlate: string
      darkRed: string
      cardBackground: string
      blueheader: string
      // Admin interface colors
      sidebarBackground: string
      sidebarHover: string
      sidebarActive: string
      contentBackground: string
      cardElevation: string
      navigationText: string
      navigationIcon: string
    }
  }
  interface PaletteOptions {
    customColors?: {
      green1?: string
      green2?: string
      green3?: string
      green4?: string
      darkSlate?: string
      darkRed?: string
      cardBackground?: string
      blueheader?: string
      // Admin interface colors
      sidebarBackground?: string
      sidebarHover?: string
      sidebarActive?: string
      contentBackground?: string
      cardElevation?: string
      navigationText?: string
      navigationIcon?: string
    }
  }
}

// Extending the Theme interface to include custom design tokens
declare module '@mui/material/styles' {
  interface Theme {
    customSpacing: {
      sidebarWidth: number
      sidebarCollapsedWidth: number
      contentPadding: number
      cardPadding: number
      navigationItemHeight: number
      pageMargin: number
      cardGap: number
      sectionGap: number
      elementGap: number
    }
    customShadows: {
      card: string
      cardHover: string
      sidebar: string
      navigation: string
    }
    customAnimations: {
      sidebarTransition: string
      navigationTransition: string
      cardTransition: string
      hoverTransition: string
    }
  }
  interface ThemeOptions {
    customSpacing?: {
      sidebarWidth?: number
      sidebarCollapsedWidth?: number
      contentPadding?: number
      cardPadding?: number
      navigationItemHeight?: number
      pageMargin?: number
      cardGap?: number
      sectionGap?: number
      elementGap?: number
    }
    customShadows?: {
      card?: string
      cardHover?: string
      sidebar?: string
      navigation?: string
    }
    customAnimations?: {
      sidebarTransition?: string
      navigationTransition?: string
      cardTransition?: string
      hoverTransition?: string
    }
  }
}

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1024, // Custom value (was 1280 by default)
      xl: 1536,
    },
  },
  // Custom design tokens for admin interface
  customSpacing: {
    sidebarWidth: 280,
    sidebarCollapsedWidth: 64,
    contentPadding: 24,
    cardPadding: 24,
    navigationItemHeight: 48,
    // Standardized spacing scale based on 8px grid
    pageMargin: 24,
    cardGap: 16,
    sectionGap: 32,
    elementGap: 8,
  },
  customShadows: {
    card: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    cardHover:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    sidebar: '2px 0 8px 0 rgba(0, 0, 0, 0.05)',
    navigation: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  },
  customAnimations: {
    sidebarTransition: 'width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
    navigationTransition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    cardTransition:
      'box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    hoverTransition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  },
  palette: {
    primary: {
      main: '#437943', // green
    },
    secondary: {
      main: '#EDF2F7', // light grey
    },
    success: {
      main: '#649264', // green
    },
    error: {
      main: '#E53E3E', // red
    },
    background: {
      default: '#EEEEEE', // light grey
      paper: '#EDF2F7', //  lighter grey
    },
    text: {
      primary: '#1A202C', // dark grey
      secondary: '#A0AEC0', // light grey
    },
    customColors: {
      green1: '#437943',
      green2: '#649264',
      green3: '#84ac85',
      green4: '#a5c5a6',
      darkSlate: '#252D3D',
      darkRed: '#e60000',
      cardBackground: '#FFFFFF',
      blueheader: '#1E476D', // dark blue
      // Admin interface colors
      sidebarBackground: '#FFFFFF',
      sidebarHover: '#F7FAFC',
      sidebarActive: '#EDF2F7',
      contentBackground: '#F8FAFC',
      cardElevation: '#FFFFFF',
      navigationText: '#2D3748',
      navigationIcon: '#718096',
    },
  },
  typography: {
    fontFamily: '"Roboto", Arial, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontFamily: '"Roboto", Arial, sans-serif',
      fontWeight: 400,
    },
    h2: {
      fontFamily: '"Roboto", Arial, sans-serif',
      fontWeight: 400,
    },
    h5: {
      fontFamily: '"Roboto", Arial, sans-serif',
      fontWeight: 300,
    },
    body1: {
      fontFamily: '"Roboto", Arial, sans-serif',
      fontWeight: 300,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        :root {
          --ag-background-color: white;
          --ag-header-background-color: #004080;
          --ag-header-foreground-color: white;
          --ag-border-color: #ddd;
          --ag-odd-row-background-color: #f9f9f9;
          --ag-font-family: "Roboto", sans-serif;
          --ag-row-height: 48px;
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          '&.MuiButton-primary': {
            backgroundColor: '#437943',
            color: '#ffffff',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            '&:hover': {
              backgroundColor: '#649264',
              boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            '&.Mui-disabled': {
              backgroundColor: '#A0AEC0',
              color: '#ffffff',
            },
          },
          '&.MuiButton-secondary': {
            backgroundColor: '#EDF2F7',
            color: '#2D3748',
            border: '1px solid #E2E8F0',
            '&:hover': {
              backgroundColor: '#E2E8F0',
              borderColor: '#CBD5E0',
            },
          },
          '&.MuiButton-danger': {
            backgroundColor: '#E53E3E',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#E32727',
              transform: 'translateY(-1px)',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#437943',
        },
      },
    },
    // Admin Layout Components
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.admin-card': {
            borderRadius: 12,
            boxShadow:
              '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            border: '1px solid #E2E8F0',
            transition:
              'box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            '&:hover': {
              boxShadow:
                '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              transform: 'translateY(-1px)',
            },
          },
          '&.admin-sidebar': {
            boxShadow: '2px 0 8px 0 rgba(0, 0, 0, 0.05)',
            borderRadius: 0,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          '&.admin-drawer': {
            borderRadius: 0,
            boxShadow: '4px 0 12px 0 rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.admin-nav-item': {
            borderRadius: 8,
            margin: '2px 8px',
            minHeight: 48,
            transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            '&:hover': {
              backgroundColor: '#F7FAFC',
              transform: 'translateX(2px)',
            },
            '&.active': {
              backgroundColor: '#EDF2F7',
              borderLeft: '3px solid #437943',
              '& .MuiListItemIcon-root': {
                color: '#437943',
              },
              '& .MuiListItemText-primary': {
                color: '#2D3748',
                fontWeight: 600,
              },
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          '&.admin-nav-icon': {
            color: '#718096',
            minWidth: 40,
            transition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          '&.admin-nav-text': {
            '& .MuiListItemText-primary': {
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#2D3748',
              transition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.admin-toggle': {
            borderRadius: 8,
            transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            '&:hover': {
              backgroundColor: '#F7FAFC',
              transform: 'scale(1.05)',
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#2D3748',
          color: '#FFFFFF',
          fontSize: '0.75rem',
          borderRadius: 6,
          padding: '8px 12px',
        },
        arrow: {
          color: '#2D3748',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          '&.admin-skeleton': {
            borderRadius: 4,
            backgroundColor: '#F7FAFC',
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.admin-page-title': {
            fontSize: '1.875rem',
            fontWeight: 700,
            color: '#1A202C',
            marginBottom: '1.5rem',
          },
          '&.admin-section-title': {
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#2D3748',
            marginBottom: '1rem',
          },
          '&.admin-card-title': {
            fontSize: '1.125rem',
            fontWeight: 600,
            color: '#2D3748',
          },
        },
      },
    },
  },
})

export default theme
